import { useEffect } from "react";
import { Group, Select, Text } from "@mantine/core";
import { Alert, Button } from "@xplatform/design-system";
import { useTranslation } from "@hooks/useTranslation";
import { useMappingBatchTargets } from "@hooks/mappingConfig";

interface MappingTargetFieldsProps {
    service: string;
    pluginId: string;
    onServiceChange: (service: string) => void;
    onPluginIdChange: (pluginId: string) => void;
}

interface Option {
    value: string;
    label: string;
}

/**
 * Mapping service + plugin pickers of a doctype form, filled from the mapping
 * service's `GET /plugins/batch-targets`. The pick is stored on the doctype as
 * is; mapping validates it again when extraction sends the batch.
 *
 * A saved value the list no longer offers stays selectable (marked unavailable)
 * so opening the form never silently changes an existing config.
 */
export const MappingTargetFields = ({ service, pluginId, onServiceChange, onPluginIdChange }: MappingTargetFieldsProps) => {
    const { t } = useTranslation();
    const targets = useMappingBatchTargets(true);
    const items = targets.data?.items ?? [];

    const withSaved = (options: Option[], saved: string): Option[] =>
        saved && !options.some((o) => o.value === saved)
            ? [...options, { value: saved, label: t("admin.documentTypes.mapping.unavailable", { value: saved }) }]
            : options;

    const serviceOptions = withSaved(items.map((i) => ({ value: i.service, label: i.service })), service);
    const plugins = items.find((i) => i.service === service)?.plugins ?? [];
    const pluginOptions = withSaved(
        plugins.map((p) => ({ value: p.plugin_id, label: `${p.plugin_id} (${p.model_name})` })),
        pluginId,
    );

    // Nothing picked yet and only one choice: pick it, so the common case is one click.
    useEffect(() => {
        if (!service && items.length === 1) onServiceChange(items[0].service);
    }, [service, items, onServiceChange]);
    useEffect(() => {
        if (service && !pluginId && plugins.length === 1) onPluginIdChange(plugins[0].plugin_id);
    }, [service, pluginId, plugins, onPluginIdChange]);

    const changeService = (next: string | null) => {
        const value = next ?? "";
        onServiceChange(value);
        // A plugin belongs to its service: drop the old pick when the service changes.
        if (value !== service) onPluginIdChange("");
    };

    return (
        <>
            {targets.error && (
                <Alert color="red" mt="sm">
                    <Group justify="space-between" wrap="nowrap">
                        <Text size="sm">{t("admin.documentTypes.mapping.loadError")} {targets.error}</Text>
                        <Button size="xs" variant="outline" onClick={targets.reload}>
                            {t("admin.documentTypes.mapping.retry")}
                        </Button>
                    </Group>
                </Alert>
            )}
            <Group grow mt="sm" align="flex-start">
                <Select
                    label={t("admin.documentTypes.mapping.service")}
                    placeholder={t("admin.documentTypes.mapping.selectService")}
                    data={serviceOptions}
                    value={service || null}
                    onChange={changeService}
                    disabled={targets.loading && !targets.data}
                    comboboxProps={{ withinPortal: false }}
                    allowDeselect={false}
                    required
                />
                <Select
                    label={t("admin.documentTypes.mapping.pluginId")}
                    placeholder={t("admin.documentTypes.mapping.selectPlugin")}
                    data={pluginOptions}
                    value={pluginId || null}
                    onChange={(next) => onPluginIdChange(next ?? "")}
                    disabled={!service}
                    description={
                        service && targets.data && plugins.length === 0
                            ? t("admin.documentTypes.mapping.noPlugins")
                            : undefined
                    }
                    comboboxProps={{ withinPortal: false }}
                    allowDeselect={false}
                    searchable
                    required
                />
            </Group>
        </>
    );
};
